import React from 'react';

/**
 * Error boundary for the lazy route chunks.
 *
 * Every route below the crossfade is code-split, which introduces a failure
 * mode the eager bundle never had: a dynamic import can reject. The common
 * cause is deploy skew — a tab opened before a redeploy asks for an old
 * hashed chunk, and the catch-all rewrite in vercel.json answers with
 * index.html, which the chunk loader refuses. A full reload cures that
 * case completely (the fresh index.html references the new hashes), so the
 * first chunk failure triggers one automatic reload. If the reload didn't
 * cure it (genuine offline, corporate proxy), we stop looping and show a
 * quiet retry screen instead of the blank page React 18 leaves behind when
 * a render error reaches the root.
 */

const RELOADED_KEY = 'muse:chunk-reloaded';

const isChunkError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'ChunkLoadError' ||
    /loading (css )?chunk|dynamically imported module|import\(\) failed/i.test(
      error.message,
    )
  );
};

type Props = { children: React.ReactNode };
type State = { failed: boolean };

class ChunkErrorBoundary extends React.Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    if (isChunkError(error) && sessionStorage.getItem(RELOADED_KEY) !== '1') {
      sessionStorage.setItem(RELOADED_KEY, '1');
      window.location.reload();
      return;
    }
    // Non-chunk errors (or a reload that didn't cure it) fall through to
    // the retry screen below.
  }

  private retry = () => {
    sessionStorage.removeItem(RELOADED_KEY);
    window.location.reload();
  };

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.25rem',
          background: 'var(--surface-bg)',
          color: 'var(--text-primary)',
          textAlign: 'center',
          padding: '2rem',
        }}
      >
        <span
          style={{
            fontSize: '0.8rem',
            letterSpacing: '0.35em',
            textTransform: 'uppercase',
            color: 'var(--text-secondary)',
          }}
        >
          Muse
        </span>
        <p style={{ maxWidth: '26rem', lineHeight: 1.6 }}>
          This page didn&rsquo;t finish loading. It usually takes one tap to
          set right.
        </p>
        <button
          type="button"
          onClick={this.retry}
          style={{
            background: 'var(--cta-parchment)',
            color: 'var(--cta-text-on-parchment)',
            border: 'none',
            borderRadius: '999px',
            padding: '0.7rem 1.8rem',
            fontSize: '0.95rem',
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}

export default ChunkErrorBoundary;
